export default function InfoSections() {
  return (
    <>
      <WhatIsSection />
      <FeaturesSection />
      <HowItWorksSection />
      <ComparisonSection />
      <SafetySection />
    </>
  );
}

function WhatIsSection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              What Is It?
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              What Is Superbuy Spreadsheet?
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              <strong>Superbuy Spreadsheet</strong> is a structured resource system designed to help international shoppers discover products faster, compare options easier, and improve their overall shopping experience. Rather than wasting hours scrolling through unorganized product listings, the Superbuy Spreadsheet approach gives you instant access to curated, categorized, and regularly updated product collections.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              Whether you are searching for the latest sneaker releases, trending streetwear pieces, luxury accessories, or everyday essentials, the Superbuy Spreadsheet methodology organizes everything into an easy-to-navigate format. Each entry includes key details so you can make informed purchasing decisions without the guesswork.
            </p>
            <p className="text-foreground-600 leading-relaxed">
              Think of it as your personal shopping assistant that has already done the research, filtered out the noise, and presented only the most relevant product options — saving you <strong>hours of manual searching</strong> and helping you discover items you might have otherwise missed entirely.
            </p>
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20overhead%20flat%20lay%20of%20a%20white%20desk%20with%20fashion%20magazine%20open%20designer%20sunglasses%20leather%20wallet%20keychain%20and%20small%20potted%20plant%20warm%20natural%20morning%20sunlight%20streaming%20across%20the%20surface%20cream%20and%20beige%20color%20palette%20soft%20linen%20fabric%20texture%20visible%20at%20the%20edge%20editorial%20lifestyle%20photography%20high%20detail%20professional%20product%20styling%20no%20people%20no%20text%20elegant%20and%20refined%20atmosphere&width=800&height=500&seq=whatis-superbuy-product-desk&orientation=landscape"
              alt="Superbuy Spreadsheet organized product discovery concept"
              className="w-full h-48 md:h-56 object-cover object-top rounded-lg mb-5"
            />
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
              Traditional Searching vs Superbuy Spreadsheet
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70">
                    <th className="text-left py-3 pr-4 font-semibold text-foreground-800">Category</th>
                    <th className="text-left py-3 pr-4 font-semibold text-foreground-500">Traditional Search</th>
                    <th className="text-left py-3 font-semibold text-primary-700">Superbuy Spreadsheet</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { cat: 'Product Discovery', trad: 'Hours of scrolling', ss: 'Instant access to curated lists' },
                    { cat: 'Organization', trad: 'Scattered bookmarks', ss: 'Structured categories & filters' },
                    { cat: 'Time Saving', trad: 'Manual research required', ss: 'Pre-researched selections' },
                    { cat: 'Comparison', trad: 'Tab-switching chaos', ss: 'Side-by-side comparisons' },
                    { cat: 'Beginner Experience', trad: 'Overwhelming & confusing', ss: 'Clear, guided navigation' },
                  ].map((row) => (
                    <tr key={row.cat} className="border-b border-background-200/70">
                      <td className="py-3 pr-4 font-medium text-foreground-800">{row.cat}</td>
                      <td className="py-3 pr-4 text-foreground-500">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-close-circle-line text-red-400 text-sm"></i>
                          {row.trad}
                        </span>
                      </td>
                      <td className="py-3 text-primary-700 font-medium">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-checkbox-circle-line text-primary-500 text-sm"></i>
                          {row.ss}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: 'ri-search-eye-line',
      title: 'Faster Product Discovery',
      desc: 'Find products without endless searching. Our organized system lets you jump directly to curated collections so you spend less time hunting and more time finding exactly what you want.',
    },
    {
      icon: 'ri-folder-5-line',
      title: 'Better Organization',
      desc: 'Save and categorize products efficiently with a clear, logical structure. No more messy bookmarks or forgotten tabs — everything is right where you expect it to be.',
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Smarter Decisions',
      desc: 'Compare products before buying with side-by-side information that highlights key differences. Make confident purchasing choices backed by organized data, not impulse.',
    },
    {
      icon: 'ri-book-open-line',
      title: 'Shopping Education',
      desc: 'Learn proven shopping methods from experienced buyers. Access guides, tips, and strategies that help you navigate international shopping with confidence and avoid common pitfalls.',
    },
    {
      icon: 'ri-refresh-line',
      title: 'Updated Resources',
      desc: 'Access fresh information regularly. Product listings and guides are continuously updated to reflect new releases, trending items, and changes in the shopping landscape.',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Beginner Friendly',
      desc: 'Easy workflow designed for everyone. Whether you are placing your first international order or your hundredth, the intuitive structure makes the process smooth and straightforward.',
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="mb-10">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20lifestyle%20product%20photography%20featuring%20neatly%20arranged%20fashion%20items%20on%20a%20light%20wooden%20surface%20including%20folded%20cashmere%20sweater%20leather%20loafers%20minimalist%20tote%20bag%20and%20gold%20tone%20jewelry%20pieces%20warm%20afternoon%20light%20creating%20gentle%20highlights%20creamy%20neutral%20backdrop%20with%20subtle%20linen%20texture%20magazine%20editorial%20style%20clean%20composition%20no%20people%20no%20text%20sophisticated%20retail%20catalog%20look&width=1200&height=450&seq=features-superbuy-lifestyle&orientation=landscape"
            alt="Superbuy Spreadsheet features lifestyle"
            className="w-full h-40 md:h-52 object-cover object-top rounded-xl mb-8"
          />
        </div>
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
            Why Choose Us
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            Why Thousands Search For Superbuy Spreadsheet
          </h2>
          <p className="text-foreground-600 text-base max-w-2xl mx-auto">
            The Superbuy Spreadsheet approach has become the go-to resource for shoppers who value efficiency, organization, and informed decision-making. Here is why it stands out.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map((feat) => (
            <div
              key={feat.title}
              className="group p-6 md:p-7 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 hover:bg-background-50 transition-all duration-200"
            >
              <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors duration-200">
                <i className={`${feat.icon} text-primary-600 text-lg`}></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{feat.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{feat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    {
      step: '01',
      icon: 'ri-search-line',
      title: 'Find Products',
      desc: 'Browse through organized categories and curated collections to discover products that match your style, needs, and budget — all in one convenient place.',
    },
    {
      step: '02',
      icon: 'ri-arrow-left-right-line',
      title: 'Compare Options',
      desc: 'Evaluate different products side by side using detailed information about quality, pricing, and availability to narrow down your best choices.',
    },
    {
      step: '03',
      icon: 'ri-file-list-3-line',
      title: 'Review Information',
      desc: 'Check comprehensive product details, read through shopping guides, and verify all the important specifications before making your decision.',
    },
    {
      step: '04',
      icon: 'ri-shopping-cart-2-line',
      title: 'Complete Purchase',
      desc: 'Once you have found the perfect item, proceed to purchase with confidence knowing you have made a well-researched and informed decision.',
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
            How It Works
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            How Superbuy Spreadsheet Works
          </h2>
          <p className="text-foreground-600 text-base max-w-2xl mx-auto">
            Getting started with the Superbuy Spreadsheet approach is simple. Follow these four straightforward steps to transform your shopping experience.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {steps.map((s, i) => (
            <div key={s.step} className="relative">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center h-full">
                <div className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                  {s.step}
                </div>
                <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className={`${s.icon} text-primary-600 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{s.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-primary-300">
                  <i className="ri-arrow-right-line text-xl"></i>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    { feature: 'Product Database Size', ss: 'Extensive curated collections', normal: 'Scattered listings' },
    { feature: 'Organization Level', ss: 'Fully structured categories', normal: 'No consistent structure' },
    { feature: 'Discovery Speed', ss: 'Instant access to finds', normal: 'Time-consuming browsing' },
    { feature: 'User Experience', ss: 'Clean, intuitive navigation', normal: 'Cluttered, inconsistent' },
    { feature: 'Shopping Support', ss: 'Guides, tips & comparisons', normal: 'No additional guidance' },
    { feature: 'Update Frequency', ss: 'Regularly refreshed', normal: 'Static, outdated links' },
    { feature: 'Beginner Accessibility', ss: 'Designed for all levels', normal: 'Requires experience' },
    { feature: 'Community Insight', ss: 'Curated by experienced buyers', normal: 'Individual guesswork' },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
            Comparison
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            Superbuy Spreadsheet vs Normal Search
          </h2>
          <p className="text-foreground-600 text-base max-w-2xl mx-auto">
            See how the Superbuy Spreadsheet approach compares to traditional product searching across every important dimension.
          </p>
        </div>

        <div className="mb-10">
          <img
            src="https://readdy.ai/api/search-image?query=Side%20by%20side%20comparison%20concept%20photography%20showing%20organized%20versus%20scattered%20product%20arrangement%20left%20side%20features%20neatly%20aligned%20fashion%20accessories%20in%20grid%20formation%20on%20white%20surface%20right%20side%20shows%20same%20items%20scattered%20randomly%20warm%20natural%20lighting%20from%20above%20cream%20backdrop%20editorial%20style%20clean%20minimalist%20composition%20no%20people%20no%20text%20visual%20metaphor%20for%20organization%20versus%20chaos&width=1000&height=400&seq=comparison-superbuy-product-shot&orientation=landscape"
            alt="Organized vs scattered product comparison"
            className="w-full h-44 md:h-56 object-cover object-top rounded-xl"
          />
        </div>

        <div className="overflow-x-auto rounded-xl border border-background-200/70">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100">
                <th className="text-left py-4 px-5 font-semibold text-foreground-800">Feature</th>
                <th className="text-left py-4 px-5 font-semibold text-primary-700">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-sm"></i>
                    Superbuy Spreadsheet
                  </span>
                </th>
                <th className="text-left py-4 px-5 font-semibold text-foreground-500">Normal Search</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.feature} className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                  <td className="py-3.5 px-5 font-medium text-foreground-800">{row.feature}</td>
                  <td className="py-3.5 px-5 text-primary-700">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-check-fill text-primary-500 text-xs"></i>
                      {row.ss}
                    </span>
                  </td>
                  <td className="py-3.5 px-5 text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-close-fill text-foreground-300 text-xs"></i>
                      {row.normal}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-8 md:p-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-shield-check-line text-accent-600 text-lg"></i>
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              Safety &amp; Security Promise
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {[
              { icon: 'ri-lock-line', title: 'Website Security', desc: 'Our website uses HTTPS encryption to protect your browsing data and ensure a secure connection during every visit.' },
              { icon: 'ri-shield-line', title: 'Safe Browsing', desc: 'We maintain a clean, malware-free browsing environment with no deceptive pop-ups or misleading redirects.' },
              { icon: 'ri-eye-off-line', title: 'Privacy Protection', desc: 'We respect your privacy. Browsing our resource pages does not require personal information or account creation.' },
              { icon: 'ri-alert-line', title: 'Avoid Fake Websites', desc: 'Always verify you are visiting the official superbuyspread.casa domain. Be cautious of copycat sites impersonating our brand.' },
            ].map((item) => (
              <div key={item.title} className="flex gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-accent-600 text-sm`}></i>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-800 text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 rounded-lg bg-secondary-100/60 border border-secondary-200/50">
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong className="text-foreground-900">Important Note:</strong> The Superbuy Spreadsheet platform provides informational resources, product discovery tools, and shopping guidance. All purchasing decisions are made independently by users on external platforms. We encourage responsible shopping practices and recommend users review product details carefully before completing any transaction.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}